import { useEffect, useState, useCallback } from "react";
import { Box, Typography, useTheme, CircularProgress } from "@mui/material";
import { tokens } from "../../../theme";
import apiService from "../../../service/Api";

type AssignedSiteVisit = {
  id: number;
  dueDate: string | null;
  venue: {
    id: number;
    name: string;
    address: string;
  };
};

type DashboardResponse = {
  assigned_site_visits: AssignedSiteVisit[];
};

type CalendarEvent = {
  id: number;
  venueName: string;
  location: string;
  date: string;
};

const generateICS = (event: CalendarEvent) => {
  const startDate = new Date();
  const endDate = new Date(startDate.getTime() + 60 * 60 * 1000); // +1 hour

  const formatICSDate = (date: Date) =>
    date.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";

  const icsContent = `
BEGIN:VCALENDAR
VERSION:2.0
BEGIN:VEVENT
SUMMARY:${event.venueName}
LOCATION:${event.location}
DTSTART:${formatICSDate(startDate)}
DTEND:${formatICSDate(endDate)}
DESCRIPTION:Site visit at ${event.venueName}
END:VEVENT
END:VCALENDAR
`;

  const blob = new Blob([icsContent], { type: "text/calendar" });
  const url = window.URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.download = `${event.venueName}.ics`;
  link.click();

  window.URL.revokeObjectURL(url);
};

const PendingSiteVisitWidget = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(false);

  // Format date nicely
  const formatDate = (dateString: string | null) => {
    if (!dateString) return "No date";

    return new Date(dateString).toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
    });
  };

  const fetchAssignedVisits = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiService.get<DashboardResponse>(
        "/api/management/dashboard/operations/",
      );

      const mappedData: CalendarEvent[] =
        response.data.assigned_site_visits.map((visit) => ({
          id: visit.id,
          venueName: visit.venue?.name || "Unknown Venue",
          location: visit.venue?.address || "No location",
          date: formatDate(visit.dueDate),
        }));

      setEvents(mappedData);
    } catch (error) {
      console.error("Failed to fetch assigned site visits:", error);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAssignedVisits();
  }, [fetchAssignedVisits]);

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 360,
        px: 2.5,
        py: 2.5,
        borderRadius: "34px",
        backgroundColor: colors.primary[900],
        border: `1px solid ${colors.primary[300]}30`,
      }}
    >
      <Typography sx={{ fontWeight: "bold", mb: 2 }}>
        Pending Site Visits
      </Typography>

      {/* Loading */}
      {loading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            py: 3,
          }}
        >
          <CircularProgress size={28} />
        </Box>
      )}

      {/* Empty State */}
      {!loading && events.length === 0 && (
        <Typography sx={{ color: "rgba(255,255,255,0.6)" }}>
          No pending site visits
        </Typography>
      )}

      {/* Events */}
      {!loading &&
        events.map((event, index) => (
          <Box
            key={event.id}
            sx={{ mb: index !== events.length - 1 ? 1.5 : 0 }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "stretch",
                gap: 1.5,
                borderRadius: "18px",
                px: 1.25,
                py: 1.2,
                background: "rgba(109, 184, 198, 0.08)",
              }}
            >
              {/* Accent line */}
              <Box
                sx={{
                  width: "4px",
                  borderRadius: "999px",
                  backgroundColor: "#9fe7f2",
                  flexShrink: 0,
                }}
              />

              {/* Content */}
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                {/* LEFT */}
                <Box>
                  <Typography
                    sx={{
                      color: "#000",
                      fontWeight: 700,
                      fontSize: "0.98rem",
                    }}
                  >
                    {event.venueName}
                  </Typography>

                  <Typography
                    sx={{
                      color: "rgba(0,0,0,0.6)",
                      fontSize: "0.85rem",
                      mt: 0.3,
                    }}
                  >
                    {event.location}
                  </Typography>
                </Box>

                {/* RIGHT */}
                <Box sx={{ textAlign: "right" }}>
                  <Typography
                    sx={{
                      color: "#000",
                      fontWeight: 700,
                      fontSize: "0.95rem",
                    }}
                  >
                    {event.date}
                  </Typography>

                  <Typography
                    onClick={() => generateICS(event)}
                    sx={{
                      mt: 0.5,
                      fontSize: "0.75rem",
                      color: "#1976d2",
                      cursor: "pointer",
                      fontWeight: 600,
                      "&:hover": {
                        textDecoration: "underline",
                      },
                    }}
                  >
                    Add to calendar
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        ))}
    </Box>
  );
};

export default PendingSiteVisitWidget;
